import React, { useState, useEffect } from "react";
import { FaTimes, FaPlus, FaTrash } from "react-icons/fa";

const QuickPaymentModal = ({ 
  showModal, 
  setShowModal, 
  updateQuickPayment,
  userId
}) => {
  const [formData, setFormData] = useState({
    name: "",
    details: [],
    price: 500
  });
  const [detailItem, setDetailItem] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (showModal) {
      fetchQuickPaymentDetails();
    }
  }, [showModal, userId]);

  const fetchQuickPaymentDetails = async () => {
    setLoading(true);
    setError("");
    
    const token = localStorage.getItem("adminToken");
    
    try {
      const endpoint = `${process.env.REACT_APP_API_BASE_URL}/api/payment/quick-payment-details${userId ? `?userId=${userId}` : ''}`;
      
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch quick payment details");

      const data = await response.json();
      setFormData({
        name: data.name || "",
        details: data.details || [],
        price: data.price || 500
      });
    } catch (error) {
      setError(error.message);
      setFormData({
        name: "",
        details: [
          "Express Processing",
          "24-Hour Turnaround",
          "Priority Support",
          "Digital Documentation",
          "Secure Payment Gateway"
        ],
        price: 500
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);
    
    const token = localStorage.getItem("adminToken");
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/update-quick-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          userId: userId
        }),
      });

      if (!response.ok) throw new Error("Failed to update quick payment details");

      const data = await response.json();
      setSuccess(true);
      
      if (updateQuickPayment) {
        updateQuickPayment(formData);
      }
      
      setTimeout(() => {
        setShowModal(false);
        setSuccess(false);
      }, 1500);
      
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDetail = () => {
    if (detailItem.trim() && formData.details.length < 8) {
      setFormData({
        ...formData,
        details: [...formData.details, detailItem.trim()]
      });
      setDetailItem("");
    }
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = formData.details.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      details: updatedDetails
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "price" ? parseFloat(value) || 0 : value
    });
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
      <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Edit Quick Payment Card</h2>
          <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><FaTimes /></button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-xl text-sm">{error}</div>}
          {success && <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-xl text-sm">Quick payment details updated successfully!</div>}
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Card Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter card name"
                required
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Price (USD)</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="1"
                step="0.01"
                required
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Card Details</label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={detailItem}
                  onChange={(e) => setDetailItem(e.target.value)}
                  placeholder="Add a detail item"
                  className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white"
                />
                <button 
                  type="button"
                  onClick={handleAddDetail}
                  disabled={formData.details.length >= 8}
                  className="px-4 py-2 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" /> Add
                </button>
              </div>
              
              {formData.details.length === 0 && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-xl text-sm">No details added yet. Add some details to display on the card.</div>
              )}
              
              <ul className="space-y-2 mt-3">
                {formData.details.map((detail, index) => (
                  <li key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-dark-700 rounded-xl">
                    <span className="text-gray-700 dark:text-gray-300">{detail}</span>
                    <button 
                      type="button"
                      onClick={() => handleRemoveDetail(index)}
                      className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-1"
                    >
                      <FaTrash className="w-3 h-3" /> Remove
                    </button>
                  </li>
                ))}
              </ul>
              
              {formData.details.length >= 8 && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-xl text-sm">Maximum of 8 detail items reached.</div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600">Cancel</button>
              <button type="submit" disabled={loading || formData.details.length === 0} className="px-6 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">{loading ? "Updating..." : "Save Changes"}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickPaymentModal;
